import { BaseScene } from './BaseScene';
import { SceneKey } from './sceneKeys';
import { GAME_WIDTH, GAME_HEIGHT } from './gameConfig';
import { GameMode } from '../types/GameMode';
import { BackgroundImageKey } from '../constants/imageKeys';
import { AudioKey } from '../constants/audioKeys';
import { playSe } from '../utils/audioManager';
import { loadGameProgress } from '../utils/gameProgressManager';
import { CHALLENGE_STAGES, getNextStageNumber } from '../constants/challengeConfig';
import { getMonsterById } from '../constants/monsters';
import { getEnemyIntroduction } from '../constants/enemyIntroductionConfig';
import {
  getStageStatus,
  StageStatus,
  STAGE_SELECT_LAYOUT,
  STAGE_SELECT_COLORS,
  getVisibleStages,
  getGridLayout,
} from './stageSelectConfig';
import { EREBOS_MONSTER, EREBOS_FORM2_MONSTER } from '../constants/bossConfig';

/**
 * ステージ選択画面シーン（キャンペーンモード）
 *
 * クリア済みステージを一覧表示し、再プレイ or 次ステージへの挑戦を選択する。
 * ステージタップ時に敵キャラ紹介ポップアップを表示する。
 */
export class StageSelectScene extends BaseScene {
  private introPopupObjects: Phaser.GameObjects.GameObject[] = [];

  constructor() {
    super(SceneKey.STAGE_SELECT);
  }

  create(): void {
    this.cameras.main.fadeIn(300, 0, 0, 0);

    if (this.textures.exists(BackgroundImageKey.TITLE)) {
      this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, BackgroundImageKey.TITLE).setOrigin(0.5);
    }

    const progress = loadGameProgress();
    const clearedStages = progress.clearedStages;

    this.createHeader();
    this.createStageGrid(clearedStages);
    this.createBackButton();
  }

  private createHeader(): void {
    this.add
      .text(GAME_WIDTH / 2, STAGE_SELECT_LAYOUT.headerY, 'ステージ選択', {
        fontSize: '32px',
        color: '#ffffff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 4,
      })
      .setOrigin(0.5);
  }

  private createStageGrid(clearedStages: number): void {
    const { gridCols, gridStartX, cellWidth } = STAGE_SELECT_LAYOUT;
    const visibleStages = getVisibleStages(clearedStages);
    const layout = getGridLayout(visibleStages.length);

    visibleStages.forEach((stage, index) => {
      const col = index % gridCols;
      const row = Math.floor(index / gridCols);
      const x = gridStartX + col * cellWidth + cellWidth / 2;
      const y = layout.gridStartY + row * layout.cellHeight + layout.cellHeight / 2;

      const status = getStageStatus(stage.stageNumber, clearedStages);
      this.createStageCell(x, y, stage.stageNumber, stage.enemyMonsterId, status, clearedStages, layout.cellHeight, !!stage.isBoss);
    });
  }

  private createStageCell(
    x: number,
    y: number,
    stageNumber: number,
    enemyMonsterId: string,
    status: StageStatus,
    clearedStages: number,
    cellHeight: number = STAGE_SELECT_LAYOUT.cellHeight,
    isBossStage: boolean = false,
  ): void {
    const { cellWidth, cellPadding } = STAGE_SELECT_LAYOUT;
    const w = cellWidth - cellPadding * 2;
    const h = cellHeight - cellPadding * 2;

    const bgColor = status === StageStatus.CLEARED
      ? STAGE_SELECT_COLORS.clearedBg
      : status === StageStatus.NEXT
        ? STAGE_SELECT_COLORS.nextBg
        : STAGE_SELECT_COLORS.lockedBg;

    const borderColor = status === StageStatus.CLEARED
      ? STAGE_SELECT_COLORS.clearedBorder
      : status === StageStatus.NEXT
        ? STAGE_SELECT_COLORS.nextBorder
        : STAGE_SELECT_COLORS.lockedBorder;

    const bg = this.add
      .rectangle(x, y, w, h, bgColor)
      .setStrokeStyle(2, borderColor);

    const textColor = status === StageStatus.LOCKED
      ? STAGE_SELECT_COLORS.lockedText
      : STAGE_SELECT_COLORS.text;

    this.add
      .text(x, y - 35, `Stage ${stageNumber}`, {
        fontSize: '20px',
        color: textColor,
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 3,
      })
      .setOrigin(0.5);

    const monster = getMonsterById(enemyMonsterId) ??
      [EREBOS_MONSTER, EREBOS_FORM2_MONSTER].find((m) => m.id === enemyMonsterId);
    const monsterName = isBossStage ? '？？？' : (monster?.name ?? enemyMonsterId);
    this.add
      .text(x, y + 5, monsterName, {
        fontSize: '16px',
        color: textColor,
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        stroke: '#000000',
        strokeThickness: 2,
      })
      .setOrigin(0.5);

    const badgeOffsetY = Math.round(cellHeight * 0.22);
    if (status === StageStatus.CLEARED) {
      const clearedLabel = isBossStage ? '★ CLEAR ★' : '✔ クリア済み';
      const clearedColor = isBossStage ? '#ffdd44' : STAGE_SELECT_COLORS.clearedBadge;
      this.add
        .text(x, y + badgeOffsetY, clearedLabel, {
          fontSize: '13px',
          color: clearedColor,
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
          fontStyle: isBossStage ? 'bold' : 'normal',
        })
        .setOrigin(0.5);
    } else if (status === StageStatus.NEXT) {
      const nextLabel = isBossStage ? '⚔ ボス戦！' : '▶ 挑戦！';
      const nextColor = isBossStage ? '#ff4444' : '#6688dd';
      this.add
        .text(x, y + badgeOffsetY, nextLabel, {
          fontSize: '13px',
          color: nextColor,
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
          fontStyle: 'bold',
        })
        .setOrigin(0.5);
    } else {
      this.add
        .text(x, y + 35, '🔒', {
          fontSize: '18px',
        })
        .setOrigin(0.5);
    }

    if (status !== StageStatus.LOCKED) {
      bg.setInteractive({ useHandCursor: true });

      bg.on('pointerover', () => {
        bg.setScale(1.05);
      });

      bg.on('pointerout', () => {
        bg.setScale(1.0);
      });

      bg.on('pointerdown', () => {
        playSe(this.sound, AudioKey.SE_SELECT);
        this.showEnemyIntroduction(stageNumber, clearedStages, status === StageStatus.CLEARED);
      });
    }
  }

  /** 敵キャラ紹介ポップアップを表示 */
  private showEnemyIntroduction(stageNumber: number, clearedStages: number, isReplay: boolean): void {
    this.closeEnemyIntroduction();

    const intro = getEnemyIntroduction(stageNumber);
    if (!intro) {
      this.proceedToCharacterSelect(stageNumber, clearedStages, isReplay);
      return;
    }

    const cx = GAME_WIDTH / 2;
    const cy = GAME_HEIGHT / 2;
    const panelW = 700;
    const panelH = 420;
    const depth = 100;

    // オーバーレイ
    const overlay = this.add
      .rectangle(cx, cy, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.75)
      .setDepth(depth)
      .setInteractive();
    this.introPopupObjects.push(overlay);

    // パネル背景
    const panel = this.add
      .rectangle(cx, cy, panelW, panelH, 0x1a1a2e)
      .setStrokeStyle(2, 0x4466aa)
      .setDepth(depth + 1);
    this.introPopupObjects.push(panel);

    const leftX = cx - panelW / 2 + 30;
    const rightX = cx + panelW / 2 - 30;
    let yPos = cy - panelH / 2 + 30;

    // タイトル: Stage N - 敵名（魂格名）
    const title = this.add
      .text(cx, yPos, `Stage ${intro.stageNumber} - ${intro.enemyName}（${intro.enemySpecies}）`, {
        fontSize: '22px',
        color: '#ffdd44',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
        wordWrap: { width: panelW - 60, useAdvancedWrap: true },
        align: 'center',
      })
      .setOrigin(0.5)
      .setDepth(depth + 2);
    this.introPopupObjects.push(title);

    yPos += 40;

    // 説明文
    const desc = this.add
      .text(leftX, yPos, intro.description, {
        fontSize: '14px',
        color: '#cccccc',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        wordWrap: { width: panelW - 60, useAdvancedWrap: true },
        lineSpacing: 4,
      })
      .setOrigin(0, 0)
      .setDepth(depth + 2);
    this.introPopupObjects.push(desc);

    yPos += desc.height + 20;

    // 得意距離
    const rangeText = this.add
      .text(leftX, yPos, `得意距離: ${intro.preferredRange}`, {
        fontSize: '15px',
        color: '#88aaff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        wordWrap: { width: panelW - 60, useAdvancedWrap: true },
      })
      .setOrigin(0, 0)
      .setDepth(depth + 2);
    this.introPopupObjects.push(rangeText);

    yPos += 28;

    // 長所
    const strengthsText = this.add
      .text(leftX, yPos, `長所: ${intro.strengths}`, {
        fontSize: '15px',
        color: '#88ff88',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        wordWrap: { width: panelW - 60, useAdvancedWrap: true },
      })
      .setOrigin(0, 0)
      .setDepth(depth + 2);
    this.introPopupObjects.push(strengthsText);

    yPos += 28;

    // 弱点
    const weaknessesText = this.add
      .text(leftX, yPos, `弱点: ${intro.weaknesses}`, {
        fontSize: '15px',
        color: '#ff8888',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        wordWrap: { width: panelW - 60, useAdvancedWrap: true },
      })
      .setOrigin(0, 0)
      .setDepth(depth + 2);
    this.introPopupObjects.push(weaknessesText);

    // ボタンエリア
    const btnY = cy + panelH / 2 - 40;

    // 「挑戦する」ボタン
    const goBtn = this.add
      .text(cx + 80, btnY, isReplay ? '再挑戦する' : '挑戦する', {
        fontSize: '20px',
        color: '#ffffff',
        backgroundColor: '#335599',
        padding: { x: 20, y: 8 },
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
      })
      .setOrigin(0.5)
      .setDepth(depth + 2)
      .setInteractive({ useHandCursor: true });
    this.introPopupObjects.push(goBtn);

    goBtn.on('pointerover', () => goBtn.setScale(1.05));
    goBtn.on('pointerout', () => goBtn.setScale(1.0));
    goBtn.on('pointerdown', () => {
      playSe(this.sound, AudioKey.SE_SELECT);
      this.closeEnemyIntroduction();
      this.proceedToCharacterSelect(stageNumber, clearedStages, isReplay);
    });

    // 「戻る」ボタン
    const backBtn = this.add
      .text(cx - 80, btnY, '戻る', {
        fontSize: '20px',
        color: '#aaaaaa',
        backgroundColor: '#333333',
        padding: { x: 20, y: 8 },
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
      })
      .setOrigin(0.5)
      .setDepth(depth + 2)
      .setInteractive({ useHandCursor: true });
    this.introPopupObjects.push(backBtn);

    backBtn.on('pointerover', () => backBtn.setScale(1.05));
    backBtn.on('pointerout', () => backBtn.setScale(1.0));
    backBtn.on('pointerdown', () => {
      playSe(this.sound, AudioKey.SE_SELECT);
      this.closeEnemyIntroduction();
    });
  }

  /** 紹介ポップアップを閉じる */
  private closeEnemyIntroduction(): void {
    this.introPopupObjects.forEach((obj) => obj.destroy());
    this.introPopupObjects = [];
  }

  /** キャラ選択画面に遷移 */
  private proceedToCharacterSelect(stageNumber: number, clearedStages: number, isReplay: boolean): void {
    const isBossStage = stageNumber === 9;
    this.transitionTo(SceneKey.CHARACTER_SELECT, {
      mode: GameMode.CHALLENGE,
      stageNumber,
      clearedStages,
      isReplay,
      isBossStage,
    });
  }

  private createBackButton(): void {
    const progress = loadGameProgress();
    const visibleStages = getVisibleStages(progress.clearedStages);
    const layout = getGridLayout(visibleStages.length);
    const text = this.add
      .text(GAME_WIDTH / 2, layout.backButtonY, '戻る', {
        fontSize: '24px',
        color: '#ffffff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        stroke: '#000000',
        strokeThickness: 5,
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });

    text.on('pointerover', () => text.setScale(1.1));
    text.on('pointerout', () => text.setScale(1.0));
    text.on('pointerdown', () => {
      playSe(this.sound, AudioKey.SE_SELECT);
      this.transitionTo(SceneKey.BATTLE_MODE_SELECT);
    });
  }
}
